/**
 * Coming-soon placeholder with feedback form. Used by the Therapist and
 * Schools top-nav tabs while those features are being built.
 *
 * Posts to /api/feedback/comingsoon — a tiny endpoint that just stores
 * the feedback row + emails support@sonspeak.com so the founder hears
 * directly from users while the feature is incubating.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Send, ArrowLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSEO } from "@/lib/seo";

export default function ComingSoonPage({ topic, title, blurb }) {
  useSEO({ title, description: blurb, path: `/${topic}` });
  const { user } = useAuth();
  const isAnon = !user;
  const [feedback, setFeedback] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const msg = feedback.trim();
    if (!msg) {
      setError("Tell us what you'd like to see — anything helps.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/feedback/comingsoon", {
        topic,
        message: msg,
        ...(isAnon ? { name: name.trim() || null, email: email.trim() || null } : {}),
      });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not send. Email support@sonspeak.com instead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--cream)] px-4 sm:px-6 py-6">
      {/* Branded header — logo + back link so every coming-soon page has visual identity */}
      <div className="max-w-3xl mx-auto flex items-center gap-3 mb-6">
        <Link
          to={user ? "/pick-board" : "/"}
          className="inline-flex items-center text-sm font-semibold text-[color:var(--ink)]/70 hover:text-[color:var(--ink)]"
          data-testid={`coming-soon-${topic}-back`}
        >
          <ArrowLeft size={16} className="mr-1" aria-hidden /> Back
        </Link>
        <div className="flex-1" />
        <Link to="/" data-testid={`coming-soon-${topic}-logo`}>
          <img
            src="/brand/sonspeak-logo-transparent.png"
            alt="SonSpeak"
            className="h-9 sm:h-10 w-auto"
          />
        </Link>
      </div>

      <div className="max-w-2xl mx-auto">
        <div className="brutal-card bg-white p-6 sm:p-8 text-center" data-testid={`coming-soon-${topic}`}>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl border-[3px] border-[color:var(--ink)] bg-[color:var(--gold)] mb-3">
            <Sparkles size={28} aria-hidden />
          </div>
          <h1 className="display text-2xl sm:text-3xl font-extrabold mb-2">{title}</h1>
          <p className="text-sm text-[color:var(--ink)]/70 mb-5">{blurb}</p>

          {sent ? (
            <div
              className="brutal-card bg-emerald-50 p-4 text-emerald-900"
              data-testid={`coming-soon-${topic}-thanks`}
            >
              <p className="font-bold mb-1">Thank you 💜</p>
              <p className="text-sm">
                Your note went straight to our inbox. We'll reach out as soon
                as we have something to show you.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="text-left">
              {isAnon && (
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <label className="block">
                    <span className="block mb-1 text-sm font-bold">Your name (optional)</span>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={120}
                      className="brutal-input w-full"
                      data-testid={`coming-soon-${topic}-name`}
                    />
                  </label>
                  <label className="block">
                    <span className="block mb-1 text-sm font-bold">Email (optional, so we can reply)</span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      maxLength={200}
                      className="brutal-input w-full"
                      data-testid={`coming-soon-${topic}-email`}
                    />
                  </label>
                </div>
              )}
              <label className="block mb-2 text-sm font-bold">
                Tell us what you need most
              </label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={4}
                maxLength={1000}
                placeholder="e.g. I'm a school SLP and I'd love to manage 12 students' boards from one dashboard…"
                className="brutal-input w-full"
                data-testid={`coming-soon-${topic}-input`}
              />
              {error && (
                <div className="text-sm text-red-600 mt-2" data-testid={`coming-soon-${topic}-error`}>
                  {error}
                </div>
              )}
              <div className="flex justify-end mt-3">
                <button
                  type="submit"
                  disabled={busy}
                  className="pill-btn primary"
                  data-testid={`coming-soon-${topic}-submit`}
                >
                  <Send size={14} /> {busy ? "Sending…" : "Send to the team"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export function TherapistPortal() {
  return (
    <ComingSoonPage
      topic="therapist"
      title="Therapist tools — coming soon"
      blurb="We're building therapist-friendly dashboards, multi-client boards, and sponsor codes. Tell us what would help you the most."
    />
  );
}

export function SchoolsPortal() {
  return (
    <ComingSoonPage
      topic="schools"
      title="Schools — coming soon"
      blurb="Classroom-friendly tools for SLPs, OTs, and special-ed teams. Help us prioritize what we build first."
    />
  );
}
